import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { REQUEST_TYPES } from "@demandproai/shared";
import useDemandStudioEditActions from "./useDemandStudioEditActions";
import { toaster } from "../utils/toast";

const hasUndoableRequests = (requestResults) =>
  Array.isArray(requestResults) && requestResults.length > 0;

const validateRequestResultsInputForUndo = (requestResults) => {
  if (!hasUndoableRequests(requestResults)) {
    throw new Error(`Invalid input to undo function: ${requestResults}`)
  }
}

const getAvailiableUndoDepth = (requestResults) => {
  if (!hasUndoableRequests(requestResults)) return 0;
  let depth = 0;
  for (let i = requestResults.length - 1; i >= 0; i--) {
    const type = requestResults[i]?.requestType;
    if (type === REQUEST_TYPES.DEMAND) break;
    if (type === REQUEST_TYPES.UNDO) {
      depth -= 1;
    } else {
      depth += 1;
    }
  }
  return depth;
}

// walks newest -> oldest; terminates at DEMAND or any non-UNDO/REDO request (older pages cannot change the outcome past those anchors)
const walkRedoStack = (requestResults) => {
  if (!hasUndoableRequests(requestResults)) return { depth: 0, terminated: false };
  let depth = 0;
  for (let i = requestResults.length - 1; i >= 0; i--) {
    const type = requestResults[i]?.requestType;
    if (type === REQUEST_TYPES.DEMAND) return { depth, terminated: true };
    if (type === REQUEST_TYPES.REDO) {
      depth -= 1;
    } else if (type === REQUEST_TYPES.UNDO) {
      depth += 1;
    } else {
      return { depth, terminated: true };
    }
  }
  return { depth, terminated: false };
}

const getAvailiableRedoDepth = (requestResults) => walkRedoStack(requestResults).depth;
const isRedoWalkTerminated = (requestResults) => walkRedoStack(requestResults).terminated;

const getNextId = (requestResults, isRedo = false) => {
  validateRequestResultsInputForUndo(requestResults);
  let depth = -1;
  for (let i = requestResults.length - 1; i >= 0; i--) {
    const type = requestResults[i]?.requestType;
    const conditionForDepthDecrease = isRedo ? (type === REQUEST_TYPES.REDO) : (type === REQUEST_TYPES.UNDO);
    if (depth === 0) {
      return requestResults[i]._id;
    } else if (conditionForDepthDecrease) {
      depth -= 1;
    } else {
      depth += 1;
    }
  }
  console.error(`Invalid ${isRedo ? 'redo' : 'undo'} stack for `, requestResults);
}

const useSnapshotUndo = ({
  caseId,
  fetchCaseDetails,
  resultRequests,
  clientId,
  hasMoreHistory = false,
  loadOlderUntil,
  onEditPersisted
}) => {
  const [isUndoLoading, setIsUndoLoading] = useState(false);
  const isLoadingOlderRef = useRef(false);
  const { sendEditActionImmediate: sendEditAction } = useDemandStudioEditActions({ caseId, onEditPersisted });

  const handleUndo = useCallback(
    async (isRedo = false) => {
      setIsUndoLoading(true);
      try {
        const requestIdToReference = getNextId(resultRequests, isRedo);
        const payload = clientId ? { requestIdToReference, clientId } : { requestIdToReference };
        const editActionResponse = await sendEditAction(payload, isRedo ? REQUEST_TYPES.REDO : REQUEST_TYPES.UNDO);
        if (editActionResponse?.success !== false) {
          await fetchCaseDetails();
        }
      } catch (error) {
        toaster({ message: `Unable to undo: ${error.message}`, success: false });
      } finally {
        setIsUndoLoading(false);
      }
    },
    [resultRequests, clientId, sendEditAction, fetchCaseDetails]
  );

  const undoDepth = useMemo(() => getAvailiableUndoDepth(resultRequests), [resultRequests]);

  // undo is available on the currently loaded pages when depth is positive and a target id resolves
  const hasUndo = useMemo(
    () => Boolean(resultRequests?.length) && undoDepth > 0 && Boolean(getNextId(resultRequests)),
    [resultRequests, undoDepth]
  );

  const hasRedo = useMemo(
    () => getAvailiableRedoDepth(resultRequests) > 0,
    [resultRequests]
  );

  const guardedLoadMore = useCallback((getIsSatisfied) => {
    if (!hasMoreHistory || !loadOlderUntil || isLoadingOlderRef.current) return;
    isLoadingOlderRef.current = true;
    Promise.resolve(
      loadOlderUntil(getIsSatisfied)
    ).finally(() => {
      isLoadingOlderRef.current = false;
    });
  }, [hasMoreHistory, loadOlderUntil]);

  // when current pages show no undo/redo, walk older pages once (guarded) so a later render can reveal it
  useEffect(() => {
    const needsUndo = !hasUndo;
    const needsRedo = !hasRedo && hasMoreHistory && !isRedoWalkTerminated(resultRequests);
    if (!needsUndo && !needsRedo) return;
    guardedLoadMore((loaded)=>{
      const undoSatisfied = !needsUndo || (getAvailiableUndoDepth(loaded) > 0 && Boolean(getNextId(loaded)));
      // recompute per page: stop as soon as redo is found OR the walk hits a terminating anchor (no more improvement possible)
      const { depth: redoDepth, terminated: redoTerminated } = walkRedoStack(loaded);
      const redoFound = redoDepth > 0 && Boolean(getNextId(loaded, true));
      const redoSatisfied = !needsRedo || redoFound || redoTerminated;
      return undoSatisfied && redoSatisfied;
    });
  }, [hasUndo, hasRedo, hasMoreHistory, guardedLoadMore]);

  return {
    handleUndo,
    isUndoLoading,
    hasUndo,
    hasRedo
  };
};

export default useSnapshotUndo;
